<template>
  <sticky-header-layout>
    <template #header>
      <div class="action-container">
        <el-button
          :disabled="isLoading || isSaving"
          class="add-editorial-item-button"
          icon="el-icon-plus"
          size="mini"
          type="primary"
          @click="handleAddEditorialItem"
        >
          {{ $t('button.addNewItem') }}
        </el-button>
        
        <span>
          <el-button
            :disabled="isSaving"
            class="cancel-button"
            icon="el-icon-close"
            size="mini"
            type="warning"
            @click="handleCancel"
          >
            {{ $t('button.cancel') }}
          </el-button>
          
          <el-button
            :disabled="isLoading"
            :loading="isSaving"
            class="save-button"
            icon="el-icon-upload2"
            size="mini"
            type="danger"
            @click="handleSave"
          >
            {{ $t('button.save') }}
          </el-button>
        </span>
      </div>
    </template>

    <div v-loading="isLoading">
      <el-form
        ref="form"
        :disabled="isSaving"
        :model="form"
        :rules="formRules"
        class="round-border-form"
        label-position="right"
        label-width="140px"
      >
        <div class="basic-info-container">
          <h4 class="title">{{ $t('table.basicInfo') }}</h4>

          <el-form-item
            :label="$t('table.title')"
            prop="title"
          >
            <el-input
              v-model="form.title"
              clearable
            />
          </el-form-item>

          <el-form-item :label="$t('table.enable')">
            <el-switch v-model="form.enabled" />
          </el-form-item>

          <el-form-item
            :label="$t('table.list')"
            v-if="isEditMode"
          >
            <el-switch v-model="form.listed" />
          </el-form-item>

          <el-form-item 
            :label="$t('table.notification_scheduled_at')"
            v-if="isEditMode"
          >
            <el-date-picker
              :disabled="!form.listed"
              v-model="form.notification_scheduled_at"
              :clearable="true"
              :placeholder="$t('placeholder.pickDay')"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
            />
          </el-form-item>

          <el-form-item :label="$t('table.date')">
            <el-date-picker
              v-model="form.created_at"
              :clearable="false"
              :placeholder="$t('placeholder.pickDay')"
              type="date"
              value-format="yyyy-MM-dd"
            />
          </el-form-item>

          <el-form-item :label="$t('table.category')">
            <el-select
              v-model="form.category_id"
              :placeholder="$t('placeholder.select')"
            >
              <el-option
                v-for="category in categories"
                :key="category.value"
                :label="category.label"
                :value="category.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('table.locations')">
            <el-select
              v-model="form.location"
              :placeholder="$t('placeholder.select')"
            >
              <el-option
                v-for="location in locations"
                :key="location.value"
                :label="location.label"
                :value="location.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item :label="$t('table.gender')">
            <el-select
              v-model="form.gender"
              :placeholder="$t('placeholder.select')"
            >
              <el-option
                v-for="gender in genders"
                :key="gender.value"
                :label="gender.label"
                :value="gender.value"
              />
            </el-select>
          </el-form-item>

          <el-form-item
            :label="$t('table.previewToken')"
            prop="previewToken"
          >
            <el-input
              v-model.trim="form.preview_token"
              clearable
            />
          </el-form-item>

          <el-form-item
            :label="$t('table.photoCredit')"
            prop="photoCredit"
          >
            <el-input
              v-model="form.photo_credit"
              clearable
            />
          </el-form-item>

          <el-form-item
            label="Facebook Title"
            prop="facebook_title"
          >
            <el-input
              v-model.trim="form.facebook_title"
              clearable
            />
          </el-form-item>

          <el-form-item
            label="Facebook Image"
            prop="facebook_image"
          >
            <el-input
              v-model.trim="form.facebook_image"
              clearable
            />

            <img 
              :src="form.facebook_image"
              class="main-img"
            >
          </el-form-item>

          <el-form-item
            label="Short URL"
            prop="short_url"
          >
            <el-link 
              :href="form.short_url"
              target="_blank"
              style="color:#0096FF;"
            >
              {{form.short_url}}
            </el-link>
          </el-form-item>

          <el-form-item
            label="Ad Name"
            prop="ad_name"
          >
            {{form.ad_name}}
          </el-form-item>

          <el-form-item
            label="U1"
            prop="u1"
          >
            <el-input
              v-model.trim="form.u1"
              clearable
            />
          </el-form-item>

          <el-form-item
            label=""
            prop=""
          >
            <el-checkbox v-model="form.is_replace_with_same_store" >
              {{ $t('table.sameStoreSimilarProducts') }}
            </el-checkbox>
            <el-checkbox v-model="form.is_replace_with_same_product">
              {{ $t('table.sameProductDifferentStore') }}
            </el-checkbox>
            <el-checkbox v-model="form.is_replace_with_same_brand">
              {{ $t('table.sameStoreBrand') }}
            </el-checkbox>
            <el-checkbox v-model="form.is_replace_with_same_category">
              {{ $t('table.sameStoreCategory') }}
            </el-checkbox>
          </el-form-item>

          <el-form-item
            :label="$t('table.smartLinks')"
            prop="smart_links"
          >
            <editorial-smart-link-list
              :smart-links="form.smart_links"
              @add="handleAddNewSmartLink"
              @remove="handleRemoveSmartLink"
              @update="handelUpdateSmartLink"
            />
          </el-form-item>
        </div>

        <div
          v-for="(content, count) in form.static_contents"
          :key="count"
        >
          <h4 class="title subtitle">{{ getTitleFromContentType(content.type) }}</h4>

          <div class="lang-box">
            <!-- All locations -->
            <div class="lang-box-item">
              <el-button
                class="all-language-button"
                size="mini"
                type="info"
                @click="handleCopyToAllLanguages(content.type, 'en')"
              >
                {{ $t('button.copyLang') }}
              </el-button>
              
              <el-form-item
                :label="$t('common.en')"
                :prop="getFormItemProp(content.type, 'en')"
              >
                <el-input
                  v-model="content.en.value"
                  type="textarea"
                  :placeholder="content.is_image ? 'https://' : ''"
                  autosize
                />
                <img
                  v-if="content.is_image"
                  :src="content.en.value"
                  class="main-img"
                />
              </el-form-item>
            </div>

            <!-- HK location -->
            <template v-if="shouldShowFormItemForLocation(2)">
              <div class="lang-box-item">
                <el-button
                  class="all-language-button"
                  size="mini"
                  type="info"
                  @click="handleCopyToAllLanguages(content.type, 'zh_hk')"
                >
                  {{ $t('button.copyLang') }}
                </el-button>
                
                <el-form-item
                  :label="$t('common.zhHK')"
                  :prop="getFormItemProp(content.type, 'zh_hk')"
                >
                  <el-input
                    v-model="content.zh_hk.value"
                    type="textarea"
                    :placeholder="content.is_image ? 'https://' : ''"
                    autosize
                  />
                  <img
                    v-if="content.is_image"
                    :src="content.zh_hk.value"
                    class="main-img"
                  />
                </el-form-item>
              </div>

              <div class="lang-box-item">
                <el-button
                  class="all-language-button"
                  size="mini"
                  type="info"
                  @click="handleCopyToAllLanguages(content.type, 'zh_cn')"
                >
                  {{ $t('button.copyLang') }}
                </el-button>
                
                <el-form-item
                  :label="$t('common.zhCN')"
                  :prop="getFormItemProp(content.type, 'zh_cn')"
                >
                  <el-input
                    v-model="content.zh_cn.value"
                    type="textarea"
                    :placeholder="content.is_image ? 'https://' : ''"
                    autosize
                  />
                  <img
                    v-if="content.is_image"
                    :src="content.zh_cn.value"
                    class="main-img"
                  />
                </el-form-item>
              </div>
            </template>

            <!-- Thai location -->
            <template v-if="shouldShowFormItemForLocation(1)">
              <div class="lang-box-item">
                <el-button
                  class="all-language-button"
                  size="mini"
                  type="info"
                  @click="handleCopyToAllLanguages(content.type, 'th_th')"
                >
                  {{ $t('button.copyLang') }}
                </el-button>
                
                <el-form-item
                  :label="$t('common.th')"
                  :prop="getFormItemProp(content.type, 'th_th')"
                >
                  <el-input
                    v-model="content.th_th.value"
                    type="textarea"
                    :placeholder="content.is_image ? 'https://' : ''"
                    autosize
                  />
                  <img
                    v-if="content.is_image"
                    :src="content.th_th.value"
                    class="main-img"
                  />
                </el-form-item>
              </div>
            </template>
          </div>
        </div>
      </el-form>

      <h4 class="title subtitle extra-margin">Editorial Items</h4>
      <el-table
        :span-method="handleTableSpan"
        :data="form.dynamic_contents"
        class="editorial-table"
        border
        fit
        highlight-current-row
        row-key="id"
      >
        <el-table-column
          type="index"
          width="35"
        />

        <el-table-column
          width="180"
          align="center"
          label="Type"
        >
          <template slot-scope="scope">
            <el-select
              v-model="scope.row.type"
              placeholder="Select"
              @change="handleEditorialTypeChange(scope.row.type, scope.$index)"
            >
              <el-option
                v-for="type in editorialTypes"
                :key="type.value"
                :label="type.label"
                :value="type.value"
              />
            </el-select>
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('common.en')"
          min-width="250"
          align="center"
        >
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.en"
              @updateForLanguage="handleCopyEditorialItemToAllLanguages(scope.$index, 'en')"
            />
          </template>
        </el-table-column>

        <el-table-column
          v-if="shouldShowFormItemForLocation(2)"
          :label="$t('common.zhHK')"
          min-width="250"
          align="center"
        >
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.zh_hk"
              @updateForLanguage="handleCopyEditorialItemToAllLanguages(scope.$index, 'zh_hk')"
            />
          </template>
        </el-table-column>

        <el-table-column
          v-if="shouldShowFormItemForLocation(2)"
          :label="$t('common.zhCN')"
          min-width="250"
          align="center"
        >
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.zh_cn"
              @updateForLanguage="handleCopyEditorialItemToAllLanguages(scope.$index, 'zh_cn')"
            />
          </template>
        </el-table-column>

        <el-table-column
          v-if="shouldShowFormItemForLocation(1)"
          :label="$t('common.th')"
          min-width="250"
          align="center"
        >
          <template slot-scope="scope">
            <editorial-main-item
              :item-type="scope.row.type"
              :products="scope.row.products"
              v-bind.sync="scope.row.th_th"
              @updateForLanguage="handleCopyEditorialItemToAllLanguages(scope.$index, 'th_th')"
            />
          </template>
        </el-table-column>

        <el-table-column
          :label="$t('table.action')"
          align="center"
          width="120"
        >
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="danger"
              icon="el-icon-delete"
              circle
              @click="handleDeleteEditorialItem(scope.row)"
            />
            <el-button
              type="primary"
              class="add-item-handler mt-2 mx-0"
              @click="handleAddEditorialItemBelow(scope.$index)"
            >
              {{ $t('button.addBelow') }}
            </el-button>
          </template>
        </el-table-column>

        <el-table-column
          align="center"
          label="Drag"
          width="55"
        >
          <template>
            <span class="drag-handler">
              <font-awesome-icon
                class="drag"
                icon="arrows-alt"
              />
            </span>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </sticky-header-layout>
</template>

<script>
import Sortable from 'sortablejs'
import EditorialMainItem from './components/EditorialMainItem.vue'
import EditorialSmartLinkList from './components/EditorialSmartLinkList.vue'

import EditorialsService from '../../api/editorials'
import { getRandomHash } from '../../utils/helper'
import { EDITORIAL_ITEMS } from '../../utils/const'
import { formatDatetime } from '../../utils/datetime'


export default {
  name: 'EditorialDetail',
  components: {
    EditorialMainItem,
    EditorialSmartLinkList,
  },
  props: {
    id: {
      type: [String, Number],
      default: undefined,
    },
  },
  data() {
    const validateNumber = (rule, _, callback) => {
      if (rule.field.includes('main_image_ratio')) {
        const ratioContent = this.form.static_contents.find(content => content.type === 'main_image_ratio')
        let value
        switch (rule.field) {
          case 'main_image_ratio_en':
            ({ value } = ratioContent.en)
            break
          case 'main_image_ratio_zh_hk':
            ({ value } = ratioContent.zh_hk)
            break
          case 'main_image_ratio_zh_cn':
            ({ value } = ratioContent.zh_cn)
            break
          case 'main_image_ratio_th_th':
            ({ value } = ratioContent.th_th)
            break
          default:
            break
        }

        if (!value || value === 0 || value === '0') {
          return callback(this.$t('error.thisFieldIsRequired'))
        }

        if (!+value) {
          return callback(this.$t('error.thisFieldMustBeANumber'))
        }
      }

      return callback()
    }

    const validateSpaces = (rule, _, callback) => {
      if (rule.field.includes('slug')) {
        const slugContent = this.form.static_contents.find(content => content.type === 'slug')
        let value
        switch (rule.field) {
          case 'slug_en':
            ({ value } = slugContent.en)
            break
          case 'slug_zh_hk':
            ({ value } = slugContent.zh_hk)
            break
          case 'slug_zh_cn':
            ({ value } = slugContent.zh_cn)
            break
          case 'slug_th_th':
            ({ value } = slugContent.th_th)
            break
          default:
            break
        }

        if (!value || value === 0 || value === '0') {
          return callback(this.$t('error.thisFieldIsRequired'))
        }

        if (/\s/.test(value)) {
          return callback('This field should not contain spaces.')
        }
      }
      return callback()
    }

    return {
      form: {
        title: '',
        enabled: false,
        listed: false,
        notification_scheduled_at: null,
        created_at: null,
        category_id: '',
        location: 2,
        gender: 'w',
        preview_token: '',
        photo_credit: '',
        ad_name: '',
        facebook_title: '',
        facebook_image: '',
        short_url: '',
        u1: '',
        is_replace_with_same_store: false,
        is_replace_with_same_product: false,
        is_replace_with_same_brand: false,
        is_replace_with_same_category: false,
        smart_links: [],
        static_contents: [
          { type: 'title', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'subtitle', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'metatitle', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'metadescription', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'main_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: 'true', },
          { type: 'main_image_ratio', en: { value: '1.5' }, zh_hk: { value: '1.5' }, zh_cn: { value: '1.5' }, th_th: { value: '1.5' }, is_image: '', },
          { type: 'home_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: 'true', },
          { type: 'cover_image', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: 'true', },
          { type: 'author', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'avator', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'category', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'tag', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
          { type: 'slug', en: { value: '' }, zh_hk: { value: '' }, zh_cn: { value: '' }, th_th: { value: '' }, is_image: '', },
        ],
        dynamic_contents: [],
      },
      formRules: {
        title: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }],
        main_image_ratio_en: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_zh_hk: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_zh_cn: [{ validator: validateNumber, trigger: 'blur' }],
        main_image_ratio_th_th: [{ validator: validateNumber, trigger: 'blur' }],
        slug_en: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_zh_hk: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_zh_cn: [{ validator: validateSpaces, trigger: 'blur' }],
        slug_th_th: [{ validator: validateSpaces, trigger: 'blur' }],
      },
      editorialTypes: EDITORIAL_ITEMS,
      locations: [
        { value: 2, label: 'EN HK CN' },
        { value: 1, label: 'EN TH' },
      ],
      genders: [
        { value: 'w', label: 'Women' },
        { value: 'm', label: 'Men' },
        { value: 'k', label: 'Kids' },
      ],
      categories: [],
      isEdited: false,
      isLoading: false,
      isSaving: false,
      sortableObject: null,
    }
  },
  computed: {
    isEditMode() {
      return !!this.id
    },
  },
  watch: {
    form: {
      handler() {
        this.isEdited = true
      },
      deep: true,
    },
  },
  mounted() {
    if (this.isEditMode) {
      this.getEditorialDetail(this.id)
    } else {
      // this.generateFormRules()
      this.$nextTick(() => {
        this.setSort()
      })
    }
  },
  beforeRouteEnter(to, from, next) {
    EditorialsService.getCategoryList().then((response) => {
      const categoryList = response.data
      const categories = [{ value: '', label: '' }];

      categoryList.forEach((category) => {
        categories.push({ value: category.id, label: category.title })
      })

      next((vm) => {
        // eslint-disable-next-line
        vm._data.categories = categories
      })
    })
  },
  beforeRouteLeave(to, from, next) {
    if (this.isSaving) {
      return next(false)
    }

    if (!this.isEdited) {
      return next()
    }

    this.$confirm(this.$t('message.leaveWithUnsavedChanges'), this.$t('common.warning'), {
      confirmButtonText: this.$t('button.leave'),
      cancelButtonText: this.$t('button.cancel'),
      type: 'warning',
    }).then(() => next()).catch(() => next(false))

    return next(false)
  },
  methods: {
    formatDatetimeToUTC(date) {
      return formatDatetime({ date: date, timezoneOffset: -8})
    },
    async getEditorialDetail(id) {
      this.isLoading = true
      try {
        const response = await EditorialsService.getDetail(id)
        const detail = response.data
        detail.enabled = !!+detail.enabled
        detail.listed = !!+detail.listed
        detail.notification_scheduled_at = detail.notification_scheduled_at ? formatDatetime({date: detail.notification_scheduled_at}) : '',
        detail.category_id = detail.category.id
        detail.is_replace_with_same_store = !!+detail.is_replace_with_same_store
        detail.is_replace_with_same_product = !!+detail.is_replace_with_same_product
        detail.is_replace_with_same_brand = !!+detail.is_replace_with_same_brand
        detail.is_replace_with_same_category = !!+detail.is_replace_with_same_category

        detail.static_contents[4].is_image = 'true' // main image link will now only accept single line input
        detail.static_contents[5].is_image = 'true' // home image link will now only accept single line input
        detail.static_contents[6].is_image = 'true' // cover image link will now only accept single line input

        // for drag and drop to render items correctly
        for (let i = 0; i < detail.dynamic_contents.length; i += 1) {
          const content = detail.dynamic_contents[i]
          content.id = getRandomHash()

          switch (content.type) {
            case 'product': {
              content.type = `${content.products.length}_product`
              break
            }
            case 'text_link':
              content.en.css = JSON.parse(content.en.css)
              content.zh_hk.css = JSON.parse(content.zh_hk.css)
              content.zh_cn.css = JSON.parse(content.zh_cn.css)
              content.th_th.css = JSON.parse(content.th_th.css)

              content.en.css['font-size'] = +content.en.css['font-size'].replace('px', '')
              content.zh_hk.css['font-size'] = +content.zh_hk.css['font-size'].replace('px', '')
              content.zh_cn.css['font-size'] = +content.zh_cn.css['font-size'].replace('px', '')
              content.th_th.css['font-size'] = +content.th_th.css['font-size'].replace('px', '')
              break
            default:
              break
          }
        }
        // detail.is_replace_with_same_brand = true
        this.form = detail
        // this.generateFormRules()
        this.$nextTick(() => {
          this.isEdited = false
          this.setSort()
        })
        this.isLoading = false
      } catch (error) {
        this.isLoading = false
        this.$alert('Sorry, the editorial was not found!', 'Error', {
          confirmButtonText: 'OK',
          type: 'error',
        }).then(() => {
          this.goToEditorialList()
        })
      }
    },
    async editEditorial(editorial, id) {
      this.isSaving = true
      const processedForm = this.convertProductType(editorial)

      try {
        await EditorialsService.updateDetail(processedForm, id)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    async createNewEditorial(editorial) {
      this.isSaving = true
      const processedForm = this.convertProductType(editorial)

      try {
        await EditorialsService.createNew(processedForm)
        this.handleSaveSuccess()
      } catch (error) {
        this.handleSaveFail(error)
      }
    },
    handleSaveSuccess() {
      this.isSaving = false
      this.isEdited = false
      this.goToEditorialList()
    },
    handleSaveFail(error) {
      this.isSaving = false
      this.$alert(`Error: ${error}`, this.$t('error.somethingWentWrong'), {
        type: 'error',
        confirmButtonText: 'OK',
      })
    },
    convertProductType(editorialForm) {
      const deepCopyEditorialForm = JSON.parse(JSON.stringify(editorialForm))
      for (let i = 0; i < deepCopyEditorialForm.dynamic_contents.length; i += 1) {
        const content = deepCopyEditorialForm.dynamic_contents[i]

        if (content.type.includes('product')) {
          content.type = 'product'
        } else if (content.type.includes('text_link')) {
          content.en.css['font-size'] += 'px'
          content.zh_hk.css['font-size'] += 'px'
          content.zh_cn.css['font-size'] += 'px'
          content.th_th.css['font-size'] += 'px'
        }
      }
      return deepCopyEditorialForm
    },
    shouldShowFormItemForLocation(locationID) {
      return this.form.location === locationID
    },
    getTitleFromContentType(contentType) {
      const titles = {
        title: this.$t('table.title'),
        subtitle: this.$t('table.subtitle'),
        metatitle: this.$t('table.metaTitle'),
        metadescription: this.$t('table.metaDescription'),
        main_image: 'Main Image (1200 x 800, Web except Homepage Cover)',
        main_image_ratio: this.$t('table.mainImageRatio'),
        home_image: 'Home Image (App Only)',
        cover_image: 'Cover Image (640 x 800, Homepage Cover Only)',
        author: this.$t('table.author'),
        avator: this.$t('table.avatar'),
        category: this.$t('table.category'),
        tag: this.$t('table.tag'),
        slug: this.$t('table.slug'),
      }
      return titles[contentType]
    },
    getFormItemProp(contentType, locale) {
      return `${contentType}_${locale}`
    },
    generateFormRules() {
      const staticContentCount = this.form.static_contents.length
      const rules = {
        title: [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }],
      }
      for (let i = 0; i < staticContentCount; i += 1) {
        const contentType = this.form.static_contents[i].type

        const enContentType = `${contentType}_en`
        const zhHKContentType = `${contentType}_zh_hk`
        const zhCNContentType = `${contentType}_zh_cn`
        const thContentType = `${contentType}_th_th`

        rules[enContentType] = [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }]
        rules[zhHKContentType] = [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }]
        rules[zhCNContentType] = [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }]
        rules[thContentType] = [{ required: true, message: this.$t('error.thisFieldIsRequired'), trigger: 'blur' }]
      }
      this.formRules = rules
      // this.$nextTick(() => {
      //   this.$refs.form.resetFields()
      // })
    },
    getNewEditorialItem(itemType = 'text') {
      const newItem = {
        id: getRandomHash(), // for drag and drop to render items correctly
        type: itemType,
      }

      switch (itemType) {
        case '1_product':
        case '2_product':
        case '3_product':
        case '4_product': {
          newItem.products = []
          const productCount = +itemType.charAt(0)
          for (let i = 0; i < productCount; i += 1) {
            newItem.products.push({
              product_id: '',
              url: '',
            })
          }
          break
        }
        case 'image_link':
          newItem.en = { value: '', link: '' }
          newItem.zh_hk = { value: '', link: '' }
          newItem.zh_cn = { value: '', link: '' }
          newItem.th_th = { value: '', link: '' }
          break
        case 'text_link':
          newItem.en = { value: '', link: '', css: { 'font-size': 35, 'font-weight': 500, 'text-decoration': 'none' } }
          newItem.zh_hk = { value: '', link: '', css: { 'font-size': 35, 'font-weight': 500, 'text-decoration': 'none' } }
          newItem.zh_cn = { value: '', link: '', css: { 'font-size': 35, 'font-weight': 500, 'text-decoration': 'none' } }
          newItem.th_th = { value: '', link: '', css: { 'font-size': 35, 'font-weight': 500, 'text-decoration': 'none' } }
          break
        case 'parallax_adsense':
          break
        case 'image':
        case 'text':
        case 'youtube':
        case 'instagram':
        default:
          newItem.en = { value: '' }
          newItem.zh_hk = { value: '' }
          newItem.zh_cn = { value: '' }
          newItem.th_th = { value: '' }
          break
      }

      return newItem
    },
    handleAddEditorialItem() {
      const newRow = this.getNewEditorialItem()
      this.form.dynamic_contents.push(newRow)
      this.$nextTick(() => {
        this.setSort()
      })
    },
    handleAddEditorialItemBelow(rowindex) {
      const newRow = this.getNewEditorialItem()
      this.form.dynamic_contents.splice(rowindex + 1, 0, newRow)
      this.$nextTick(() => {
        this.setSort()
      })
    },
    handleEditorialTypeChange(editorialType, itemsIndex) {
      const newItem = this.getNewEditorialItem(editorialType)
      this.$set(this.form.dynamic_contents, itemsIndex, newItem)
    },
    handleDeleteEditorialItem(editorialItem) {
      const index = this.form.dynamic_contents.indexOf(editorialItem)
      this.form.dynamic_contents.splice(index, 1)
      this.$nextTick(() => {
        this.setSort()
      })
    },
    handleTableSpan({ row, columnIndex }) {
      // For type with product, we don't have different languages, so just let the users to enter the content one time.
      // Check detail: http://element.eleme.io/#/en-US/component/table
      if (row.type.includes('product')) {
        switch (this.form.location) {
          case 1:
            if (columnIndex > 1 && columnIndex < 4) {
              if (columnIndex === 2) {
                return [1, 2]
              }
              return [0, 0]
            }
            break
          case 2:
            if (columnIndex > 1 && columnIndex < 7) {
              if (columnIndex === 2) {
                return [1, 5]
              }
              return [0, 0]
            }
            break
          default:
            break
        }
      }
      return undefined
    },
    goToEditorialList() {
      this.$router.push({ path: '/editorial/list' })
    },
    handleCancel() {
      this.goToEditorialList()
    },
    handleCopyEditorialItemToAllLanguages(row, content_language) {
      const editorial_item_object = this.form.dynamic_contents[row]
      const valueTypes = []
      if (typeof editorial_item_object.en.value !== 'undefined') {
        valueTypes.push('value')
      }
      if (typeof editorial_item_object.en.link !== 'undefined') {
        valueTypes.push('link')
      }
      if (typeof editorial_item_object.en.css !== 'undefined') {
        valueTypes.push('css')
      }
      for (let i = 0; i < valueTypes.length; i += 1) {
        const value = valueTypes[i]
        if (editorial_item_object[content_language][value]) {
          switch (this.form.location) {
            case 2:
              if (content_language === 'en' || content_language === 'zh_hk' || content_language === 'zh_cn') {
                if (content_language !== 'en') {
                  if (value !== 'css') { editorial_item_object.en[value] = editorial_item_object[content_language][value] } else {
                    editorial_item_object.en[value]['font-size'] = editorial_item_object[content_language][value]['font-size']
                    editorial_item_object.en[value]['font-weight'] = editorial_item_object[content_language][value]['font-weight']
                    editorial_item_object.en[value]['text-decoration'] = editorial_item_object[content_language][value]['text-decoration']
                  }
                }
                
                if (content_language !== 'zh_hk') {
                  if (value !== 'css') { editorial_item_object.zh_hk[value] = editorial_item_object[content_language][value] } else {
                    editorial_item_object.zh_hk[value]['font-size'] = editorial_item_object[content_language][value]['font-size']
                    editorial_item_object.zh_hk[value]['font-weight'] = editorial_item_object[content_language][value]['font-weight']
                    editorial_item_object.zh_hk[value]['text-decoration'] = editorial_item_object[content_language][value]['text-decoration']
                  }
                }

                if (content_language !== 'zh_cn') {
                  if (value !== 'css') { editorial_item_object.zh_cn[value] = editorial_item_object[content_language][value] } else {
                    editorial_item_object.zh_cn[value]['font-size'] = editorial_item_object[content_language][value]['font-size']
                    editorial_item_object.zh_cn[value]['font-weight'] = editorial_item_object[content_language][value]['font-weight']
                    editorial_item_object.zh_cn[value]['text-decoration'] = editorial_item_object[content_language][value]['text-decoration']
                  }
                }
              }
              break
            case 1:
              if (content_language === 'en' || content_language === 'th_th') {
                if (content_language === 'en') {
                  if (typeof css === 'undefined') { editorial_item_object.th_th[value] = editorial_item_object[content_language][value] } else {
                    editorial_item_object.th_th[value]['font-size'] = editorial_item_object[content_language][value]['font-size']
                    editorial_item_object.th_th[value]['font-weight'] = editorial_item_object[content_language][value]['font-weight']
                    editorial_item_object.th_th[value]['text-decoration'] = editorial_item_object[content_language][value]['text-decoration']
                  }
                }

                if (content_language === 'th_th') {
                  if (typeof css === 'undefined') { editorial_item_object.en[value] = editorial_item_object[content_language][value] } else {
                    editorial_item_object.en[value]['font-size'] = editorial_item_object[content_language][value]['font-size']
                    editorial_item_object.en[value]['font-weight'] = editorial_item_object[content_language][value]['font-weight']
                    editorial_item_object.en[value]['text-decoration'] = editorial_item_object[content_language][value]['text-decoration']
                  }
                }
              }
              break
            default:
              break
          }// switch
        }// prevents overwriting with empty strings
      }// for
    },
    handleCopyToAllLanguages(content_type, content_language) {
      const content_object = this.form.static_contents.find(content => content.type === content_type)
      if (content_object[content_language].value) {
        switch (this.form.location) {
          case 2:
            if (content_language === 'en' || content_language === 'zh_hk' || content_language === 'zh_cn') {
              if (content_language !== 'en') {
                content_object.en.value = content_object[content_language].value
              }
              if (content_language !== 'zh_hk') {
                content_object.zh_hk.value = content_object[content_language].value
              }
              if (content_language !== 'zh_cn') {
                content_object.zh_cn.value = content_object[content_language].value
              }
            }
            break
          case 1:
            if (content_language === 'en' || content_language === 'th_th') {
              if (content_language === 'en') {
                content_object.th_th.value = content_object.en.value
              } else {
                content_object.en.value = content_object.th_th.value
              }
            }
            break
          default:
            break
        }// switch
      }
    },
    handleSave() {
      this.$refs.form.validate().then(() => {
        if (!this.form.listed) {
          this.form.notification_scheduled_at = null
        } else {
          if (this.form.notification_scheduled_at) {
            this.form.notification_scheduled_at = this.formatDatetimeToUTC(this.form.notification_scheduled_at)
          }
        }
        
        if (this.isEditMode) {
          this.editEditorial(this.form, this.id)
        } else {
          this.createNewEditorial(this.form)
        }
      }).catch(() => {
        this.$alert(this.$t('error.pleaseFillInAndCheckErrors'), 'Oops..', {
          confirmButtonText: 'OK',
        })
      })
    },
    setSort() {
      const el = document.querySelectorAll('.el-table__body-wrapper > table > tbody')[0]
      this.sortableObject = Sortable.create(el, {
        ghostClass: 'sortable-ghost',
        handle: '.drag-handler',
        setData(dataTransfer) {
          dataTransfer.setData('Text', '')
          // to avoid Firefox bug
          // Detail see : https://github.com/RubaXa/Sortable/issues/1012
        },
        onEnd: (evt) => {
          const targetRow = this.form.dynamic_contents.splice(evt.oldIndex, 1)[0]
          this.form.dynamic_contents.splice(evt.newIndex, 0, targetRow)
        },
      })
    },
    handleAddNewSmartLink(newSmartLink) {
      this.form.smart_links.push(newSmartLink)
    },
    handleRemoveSmartLink(oldSmartLink) {
      this.form.smart_links = this.form.smart_links.filter(link => link.id !== oldSmartLink.id)
    },
    handelUpdateSmartLink(smartLink) {
      this.form.smart_links = this.form.smart_links.map((link) => {
        if (link.id === smartLink.id) return smartLink
        return link
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~@/assets/styles/view-base.scss";
@import "~@/assets/styles/table.scss";
@import "~@/assets/styles/form.scss";
.all-language-button {
  display: block !important;
  margin-left: auto !important;
  font-size: 10px !important;
  margin-bottom: 4px;
}
.editorial-table {
  border-radius: 5px;
}
.tags-container {
  width: 100%;
  .el-tag + .el-tag {
    margin-left: 10px;
  }
}
.space-between-container {
  display: flex;
  justify-content: space-between;
  align-content: center;
}
.subtitle {
  margin: 10px auto;
}
.extra-margin {
  margin-top: 30px;
}
.main-img {
  width: 200px;
}
.lang-box-item {
  width: 33%;
}

</style>
